<template>
    <div class="openapi-page" v-loading="state.fullLoading">
        <el-card shadow="never">
            <template #header>
                <div class="page-header">
                    <span>接口配置</span>
                    <el-button type="primary" @click="openDialog()">新增接口</el-button>
                </div>
            </template>

            <el-form :model="search" class="search-form" label-width="90px" @submit.prevent>
                <el-row :gutter="16">
                    <el-col :span="6"><el-form-item label="接口编码"><el-input v-model="search.interfaceCode" clearable @keyup.enter="searchData" /></el-form-item></el-col>
                    <el-col :span="6"><el-form-item label="接口名称"><el-input v-model="search.interfaceName" clearable @keyup.enter="searchData" /></el-form-item></el-col>
                    <el-col :span="6"><el-form-item label="产品类型"><el-input v-model="search.productType" clearable @keyup.enter="searchData" /></el-form-item></el-col>
                    <el-col :span="6"><el-form-item label="状态"><el-select v-model="search.status" clearable style="width: 100%"><el-option label="启用" value="ENABLED" /><el-option label="停用" value="DISABLED" /></el-select></el-form-item></el-col>
                </el-row>
                <div class="search-actions">
                    <el-button type="primary" @click="searchData">查询</el-button>
                    <el-button @click="resetSearch">重置</el-button>
                </div>
            </el-form>

            <el-table :data="tableData" border height="calc(100vh - 365px)" v-loading="state.tableLoading">
                <el-table-column prop="interfaceCode" label="接口编码" min-width="210" show-overflow-tooltip />
                <el-table-column prop="interfaceName" label="接口名称" min-width="160" />
                <el-table-column prop="handlerName" label="处理类" min-width="220" show-overflow-tooltip />
                <el-table-column prop="productType" label="产品类型" width="120" />
                <el-table-column prop="status" label="状态" width="90">
                    <template #default="{ row }">
                        <el-tag :type="row.status === 'ENABLED' ? 'success' : 'info'">{{ row.status === 'ENABLED' ? '启用' : '停用' }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="remark" label="备注" min-width="220" show-overflow-tooltip />
                <el-table-column prop="updateTime" label="更新时间" min-width="170" />
                <el-table-column label="操作" fixed="right" width="90">
                    <template #default="{ row }"><el-button size="small" @click="openDialog(row)">编辑</el-button></template>
                </el-table-column>
            </el-table>

            <div class="pagination-box">
                <el-pagination
                    background
                    layout="total, sizes, prev, pager, next"
                    v-model:current-page="pagination.current"
                    v-model:page-size="pagination.size"
                    :total="pagination.total"
                    :page-sizes="pagination.sizes"
                    @current-change="loadData"
                    @size-change="loadData" />
            </div>
        </el-card>

        <el-dialog v-model="dialog.visible" :title="dialog.form.id ? '编辑接口' : '新增接口'" width="560px">
            <el-form :model="dialog.form" label-width="100px">
                <el-form-item label="接口编码"><el-input v-model="dialog.form.interfaceCode" placeholder="例如 ACCIDENT_CHENGHAO" /></el-form-item>
                <el-form-item label="接口名称"><el-input v-model="dialog.form.interfaceName" placeholder="例如 成浩出险" /></el-form-item>
                <el-form-item label="处理类"><el-input v-model="dialog.form.handlerName" placeholder="例如 ChenghaoAccidentInterface" /></el-form-item>
                <el-form-item label="产品类型"><el-input v-model="dialog.form.productType" placeholder="例如 ACCIDENT" /></el-form-item>
                <el-form-item label="状态"><el-radio-group v-model="dialog.form.status"><el-radio label="ENABLED">启用</el-radio><el-radio label="DISABLED">停用</el-radio></el-radio-group></el-form-item>
                <el-form-item label="备注"><el-input v-model="dialog.form.remark" type="textarea" :rows="3" /></el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="dialog.visible = false">取消</el-button>
                <el-button type="primary" @click="saveData">保存</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { ElMessage } from "element-plus";
import { getOpenApiInterfacePage, saveOpenApiInterface } from "@/api/openapi";

const emptySearch = () => ({ interfaceCode: "", interfaceName: "", productType: "", status: "" });
const search = reactive(emptySearch());
const state = reactive({ tableLoading: false, fullLoading: false });
const tableData = ref([]);
const pagination = reactive({ current: 1, size: 20, total: 0, sizes: [20, 50, 100] });
const dialog = reactive({ visible: false, form: {} });

function loadData() {
    state.tableLoading = true;
    getOpenApiInterfacePage({ ...search, current: pagination.current, size: pagination.size })
        .then((res) => {
            tableData.value = res.data.records || [];
            pagination.total = res.data.total || 0;
        })
        .finally(() => state.tableLoading = false);
}

function searchData() {
    pagination.current = 1;
    loadData();
}

function resetSearch() {
    Object.assign(search, emptySearch());
    searchData();
}

function openDialog(row) {
    dialog.form = row
        ? { ...row }
        : { status: "ENABLED", productType: "ACCIDENT" };
    dialog.visible = true;
}

function saveData() {
    if (!dialog.form.interfaceCode || !dialog.form.interfaceName || !dialog.form.handlerName) {
        ElMessage.warning("接口编码、接口名称和处理类不能为空");
        return;
    }
    state.fullLoading = true;
    saveOpenApiInterface(dialog.form)
        .then(() => {
            ElMessage.success("保存成功");
            dialog.visible = false;
            loadData();
        })
        .finally(() => state.fullLoading = false);
}

onMounted(loadData);
</script>

<style lang="scss" scoped>
.openapi-page { padding: 16px; }
.page-header { display: flex; align-items: center; justify-content: space-between; font-weight: 600; }
.search-form { margin-bottom: 12px; }
.search-actions { text-align: right; }
.pagination-box { margin-top: 14px; display: flex; justify-content: flex-end; }
</style>
